import { useCallback, useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { P2PHeader, P2PPage } from "@/components/p2p/P2PSubNav";
import { Drawer, EmptyRow, Field, Notice, SkeletonRows, inputCls } from "@/components/paisa/ui";
import { api } from "@/lib/api";

const PM_TYPES = [
  { value: "UPI", label: "UPI" },
  { value: "IMPS", label: "IMPS" },
  { value: "BANK", label: "Bank transfer" },
  { value: "PAYTM", label: "Paytm" },
  { value: "PHONEPE", label: "PhonePe" },
  { value: "GPAY", label: "GPay" },
];
const errText = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";

function maskAccount(n = "") {
  return n.length > 4 ? `•••• ${n.slice(-4)}` : n;
}

export default function P2PPaymentMethods() {
  const [pms, setPms] = useState(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/p2p/payment-methods");
      setPms(data.payment_methods || []);
    } catch (e) {
      setError(errText(e));
      setPms((p) => p || []);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function remove(id) {
    if (!window.confirm("Remove this payment method?")) return;
    try {
      await api.delete(`/api/p2p/payment-methods/${id}`);
      load();
    } catch (e) {
      setError(errText(e));
    }
  }

  async function makeDefault(id) {
    try {
      await api.patch(`/api/p2p/payment-methods/${id}`, { is_default: true });
      load();
    } catch (e) {
      setError(errText(e));
    }
  }

  return (
    <AppShell bare>
      <P2PPage>
        <P2PHeader subtitle="UPI and bank accounts you use to send or receive INR on P2P trades.">
          <button
            type="button"
            onClick={() => setAdding(true)}
            data-testid="pm-add"
            className="brand-gradient rounded-lg px-3.5 py-2 text-xs font-semibold text-primary-foreground"
          >
            Add method
          </button>
        </P2PHeader>

        {error && <Notice tone="error" className="mt-4">{error}</Notice>}

        <div className="mt-6 overflow-hidden rounded-xl border border-border bg-surface/40">
          {pms === null ? (
            <SkeletonRows h="h-12" />
          ) : pms.length === 0 ? (
            <EmptyRow>No payment methods yet. Add a UPI ID or bank account to start trading.</EmptyRow>
          ) : (
            pms.map((p) => (
              <div key={p.pm_id} data-testid={`pm-row-${p.pm_id}`} className="interactive-row flex flex-wrap items-center gap-4 border-b border-border px-4 py-4 last:border-b-0">
                <span className="tile-icon flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-[10px] font-semibold text-primary">
                  {p.type}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    {p.display_name}
                    {p.is_default && <span className="rounded-md bg-primary/15 px-1.5 py-0.5 text-[10px] text-primary">Default</span>}
                    {p.verified && <span className="rounded-md bg-up/[0.12] px-1.5 py-0.5 text-[10px] text-up">Verified</span>}
                  </p>
                  <p className="num mt-0.5 text-xs text-muted-foreground">
                    {p.upi_id || `${p.bank_name || "Bank"} · ${maskAccount(p.account_number)} · ${p.ifsc || ""}`}
                  </p>
                  <p className="text-xs text-muted-foreground">Holder: {p.holder_name}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  {!p.is_default && (
                    <button
                      type="button"
                      onClick={() => makeDefault(p.pm_id)}
                      data-testid={`pm-default-${p.pm_id}`}
                      className="rounded-lg border border-border px-2.5 py-1 text-xs text-muted-foreground transition hover:border-primary hover:text-foreground"
                    >
                      Set default
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => remove(p.pm_id)}
                    data-testid={`pm-delete-${p.pm_id}`}
                    className="rounded-lg border border-down/40 px-2.5 py-1 text-xs text-down transition hover:bg-down/10"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {adding && (
          <AddMethodDrawer
            onClose={(refresh) => {
              setAdding(false);
              if (refresh) load();
            }}
          />
        )}
      </P2PPage>
    </AppShell>
  );
}

function AddMethodDrawer({ onClose }) {
  const [type, setType] = useState("UPI");
  const [form, setForm] = useState({ display_name: "", upi_id: "", holder_name: "", bank_name: "", account_number: "", ifsc: "", is_default: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k, fn = (v) => v) => (e) => setForm((f) => ({ ...f, [k]: fn(e.target.value) }));
  const isUPI = ["UPI", "PAYTM", "PHONEPE", "GPAY"].includes(type);

  async function submit() {
    setBusy(true);
    setError("");
    try {
      await api.post("/api/p2p/payment-methods", { ...form, type });
      onClose(true);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      title="Add payment method"
      subtitle="The account holder name must match your KYC."
      onClose={() => onClose(false)}
      testId="pm-drawer"
      width="max-w-lg"
      footer={
        <button
          type="button"
          onClick={submit}
          disabled={busy || !form.display_name || !form.holder_name}
          data-testid="pm-save"
          className="brand-gradient w-full rounded-lg py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-40"
        >
          {busy ? "Saving…" : "Save method"}
        </button>
      }
    >
      <div className="space-y-4">
        <Field label="Type">
          <div className="flex flex-wrap gap-1.5">
            {PM_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setType(t.value)}
                data-testid={`pm-type-${t.value}`}
                className={`rounded-full border px-3 py-1 text-xs transition ${
                  type === t.value ? "border-primary text-foreground" : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Display name">
          <input value={form.display_name} onChange={set("display_name")} placeholder="HDFC UPI" data-testid="pm-display-name" className={inputCls} />
        </Field>
        <Field label="Account holder name">
          <input value={form.holder_name} onChange={set("holder_name")} data-testid="pm-holder" className={inputCls} />
        </Field>
        {isUPI ? (
          <Field label="UPI ID">
            <input value={form.upi_id} onChange={set("upi_id")} placeholder="name@bank" data-testid="pm-upi-id" className={inputCls} />
          </Field>
        ) : (
          <>
            <Field label="Bank name">
              <input value={form.bank_name} onChange={set("bank_name")} data-testid="pm-bank" className={inputCls} />
            </Field>
            <Field label="Account number">
              <input value={form.account_number} onChange={set("account_number")} data-testid="pm-account" className={`num ${inputCls}`} />
            </Field>
            <Field label="IFSC code">
              <input value={form.ifsc} onChange={set("ifsc", (v) => v.toUpperCase())} maxLength={11} data-testid="pm-ifsc" className={`num uppercase ${inputCls}`} />
            </Field>
          </>
        )}
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.is_default}
            onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))}
            data-testid="pm-default-check"
            className="accent-[oklch(var(--primary))]"
          />
          Use as my default method
        </label>
        {error && <Notice tone="error">{error}</Notice>}
      </div>
    </Drawer>
  );
}
